import React, {useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { useClickOutside } from 'primereact/hooks';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import {
  Tree,
  TreeCheckboxSelectionKeys,
  TreeMultipleSelectionKeys,
  TreeSelectionEvent
} from 'primereact/tree';

import { treeSample } from '../../constants';
import { TreeDropdownCheckboxProps, TreeNode } from '../../types/interfaces';
import {
  findNodesByKey,
  findNodeWithParent,
  hasChildren,
  limitTreeDepth
} from '../../utils';
import '../css/DataTableDropdown.css';

const TreeDropdownCheckbox: React.FC<TreeDropdownCheckboxProps> = ({
  selected,
  className,
  overlayClassName,
  customWidth,
  treeDropdownPlaceholder,
  onSelectionChange,
  toDelete,
  value,
  withoutHr
}) => {
  const [selectedKey, setSelectedKey] = useState<
    TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null
  >(null);
  const [metaKey] = useState<boolean>(false);
  const op = useRef<OverlayPanel>(null);
  const overlayElRef = useRef<HTMLElement>(null);
  const toast = useRef<Toast>(null);
  const isExternalUpdate = useRef(false);

  useClickOutside(overlayElRef, () => {
    if (overlayElRef.current) {
      op.current?.hide();
    }
  });

  const limitedTreeNodes = useMemo(
    () => limitTreeDepth(value ?? treeSample, 10),
    [value]
  );

  const markPartialChecks = (
    nodes: TreeNode[],
    selectedKeys: TreeCheckboxSelectionKeys
  ) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        markPartialChecks(node.children, selectedKeys);

        let totalChecked = 0;
        let totalPartial = 0;
        node.children.forEach((child) => {
          if (selectedKeys[child.key]?.checked) {
            totalChecked++;
          }
          if (selectedKeys[child.key]?.partialChecked) {
            totalPartial++;
          }
        });

        if (totalChecked > 0 && totalChecked < node.children.length) {
          selectedKeys[node.key] = {
            checked: false,
            partialChecked: true
          };
        } else if (totalPartial > 0) {
          selectedKeys[node.key] = {
            checked: false,
            partialChecked: true
          };
        } else if (totalChecked === node.children.length) {
          selectedKeys[node.key] = {
            checked: true,
            partialChecked: false
          };
        }
      }
    });
  };

  
  const handleSelectionUpdate = (
    newSelection: TreeCheckboxSelectionKeys | null
  ) => {
    if (!newSelection) {
      onSelectionChange?.([]);
      return;
    }

    const keysArray = Object.keys(newSelection).filter(
      (key) => newSelection[key].checked
    );

    const isLeafNode = (node: TreeNode) =>
      !hasChildren(node, value ?? limitedTreeNodes);
    const leafNodes = findNodesByKey(value ?? limitedTreeNodes, keysArray)
      .filter(isLeafNode)
      .map((node) => ({ label: node.label, key: node.key }));

    onSelectionChange?.(leafNodes);
  };

  useEffect(() => {
    if (toDelete) {
      const updatedSelectionKeys = {
        ...(selectedKey as TreeCheckboxSelectionKeys)
      };

      const removeParentChain = (key: string) => {
        const { parent } = findNodeWithParent(value ?? limitedTreeNodes, key);
        if (parent) {
          delete updatedSelectionKeys[parent.key];
          removeParentChain(parent.key);
        }
      };

      delete updatedSelectionKeys[toDelete.toString()];
      removeParentChain(toDelete.toString());

      updateParentNodesState(updatedSelectionKeys);
      setSelectedKey(updatedSelectionKeys);
      handleSelectionUpdate(updatedSelectionKeys);
    }
  }, [toDelete]);

  useEffect(() => {
    const leafNodes = selected?.filter((n) => !n.children?.length) || [];
    const newKeys = leafNodes.reduce(
      (acc, n) => ({
        ...acc,
        [n.key]: { checked: true, partialChecked: false }
      }),
      {} as TreeCheckboxSelectionKeys
    );

    if (JSON.stringify(selectedKey) !== JSON.stringify(newKeys)) {
      updateParentNodesState(newKeys);
      setSelectedKey(newKeys);
    }
  }, [selected]);

  const updateParentNodesState = (
    updatedSelectionKeys: TreeCheckboxSelectionKeys
  ) => {
    const traverseAndUpdate = (node: TreeNode) => {
      if (node.children && node.children.length > 0) {
        let checkedCount = 0;
        let partialCheckedCount = 0;
        let hasExistingChildren = false;

        node.children.forEach((child) => {
          traverseAndUpdate(child);

          if (updatedSelectionKeys[child.key]) {
            hasExistingChildren = true;
            if (updatedSelectionKeys[child.key].checked) {
              checkedCount++;
            }
            if (updatedSelectionKeys[child.key].partialChecked) {
              partialCheckedCount++;
            }
          }
        });

        if (!hasExistingChildren) {
          delete updatedSelectionKeys[node.key];
          return;
        }

        if (checkedCount === node.children.length) {
          updatedSelectionKeys[node.key] = {
            checked: true,
            partialChecked: false
          };
        } else if (checkedCount > 0 || partialCheckedCount > 0) {
          updatedSelectionKeys[node.key] = {
            checked: false,
            partialChecked: true
          };
        } else {
          delete updatedSelectionKeys[node.key];
        }
      }
    };

    (value ?? limitedTreeNodes).forEach(traverseAndUpdate);
    setSelectedKey({ ...updatedSelectionKeys });
  };

  const handleTreeSelection = (event: TreeSelectionEvent) => {
    isExternalUpdate.current = true;
    const newSelection = event.value as TreeCheckboxSelectionKeys;
    updateParentNodesState(newSelection);
    setSelectedKey(newSelection);
    handleSelectionUpdate(newSelection);
  };

  return (
    <div
      style={{ width: customWidth }}
      className={`flex flex-column align-items-center data-table-dropdown ${className}`}
    >
      <Toast ref={toast} />
      <div className="custom-btn">
        <Button
          outlined
          type="button"
          label={treeDropdownPlaceholder}
          onClick={(e) => op.current?.toggle(e)}
          icon="pi pi-chevron-down"
          iconPos="right"
          className={selected?.length! > 0 ? 'pt-0 pb-0' : ''}
        >
          {selected?.length! > 0 && (
            <div
              className="flex align-items-center justify-content-center hover-clear-button"
              style={{
                height: '40px',
                width: '42px',
                color: '#EF4444',
                transition: 'background-color 0.2s',
                borderRadius: '4px' // Optional: match your button's border radius
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedKey(null);
                onSelectionChange?.([]);
              }}
            >
              <i className="pi pi-times"></i>
            </div>
          )}
        </Button>
      </div>
      <OverlayPanel
        className={`treeDropdownCheckbox${
          withoutHr ? ' treeDropdownCheckbox--without-hr ' : ' ' + overlayClassName
        }`}
        style={{ padding: 0, width: '400px' }}
        ref={op}
        onShow={() => {
          (overlayElRef as React.MutableRefObject<HTMLElement | null>).current =
            op.current?.getElement() ?? null;
        }}
        onHide={() => {
          (overlayElRef as React.MutableRefObject<HTMLElement | null>).current = null;
        }}
        showCloseIcon
        dismissable={false}
      >
        <Tree
          style={{ height: '400px' }}
          value={value ?? (limitedTreeNodes as TreeNode[])}
          metaKeySelection={metaKey}
          selectionMode="checkbox"
          selectionKeys={selectedKey}
          onSelectionChange={handleTreeSelection}
          filter
          filterMode="lenient"
          filterPlaceholder="Поиск по дереву"
          className="my-tree w-full"
        />
      </OverlayPanel>
    </div>
  );
};

export default TreeDropdownCheckbox;
